import { icons, type LucideIcon } from 'lucide-react'
import * as lucideExports from 'lucide-react'
import type { ReactNode } from 'react'

/** Deterministic muted background per name so fallback avatars are stable. */
const AVATAR_BG = ['bg-secondary', 'bg-muted', 'bg-accent'] as const
export function bgFor(name: string): string {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) | 0
  return AVATAR_BG[Math.abs(h) % AVATAR_BG.length]
}

type IconDrawer = {
  /** `glyphClass` sizes a drawn glyph (lucide); default h-4 w-4 */
  draw: (payload: string, glyphClass?: string) => ReactNode
  className: string
}

function flagEmoji(code: string): string | null {
  if (!/^[A-Z]{2}$/.test(code)) return null
  return String.fromCodePoint(...[...code].map((letter) => 127397 + letter.charCodeAt(0)))
}

/** Lucide's own alias exports (a renamed icon's old name), as components; null for anything else. */
function lucideAlias(name: string): LucideIcon | null {
  const found = (lucideExports as Record<string, unknown>)[name]
  return found && typeof found === 'object' && found !== null && '$$typeof' in found ? found as LucideIcon : null
}

export const ICON_DRAWERS: Record<string, IconDrawer> = {
  lucide: { draw: (name, glyphClass = 'h-4 w-4') => {
    // Lucide occasionally retires numbered aliases (Globe2 → Globe). Existing
    // metadata remains valid, so resolve that mechanical alias before falling back.
    // Lucide also renames icons (UserCircle2 → CircleUser) and keeps the old names as its own aliases:
    // served metadata naming an alias (the member type's lucide:UserCircle2) draws through them.
    const canonical = icons[name as keyof typeof icons]
      ?? icons[name.replace(/\d+$/, '') as keyof typeof icons]
      ?? lucideAlias(name)
    const Lucide = canonical
    return Lucide ? <Lucide className={glyphClass} /> : null
  }, className: '' },
  flag: { draw: (code) => flagEmoji(code), className: '' },
  initials: {
    draw: (value) => value,
    className: 'rounded-sm bg-muted text-[9px] font-semibold',
  },
  emoji: { draw: (value) => value, className: '' },
}

/** The icon's own glyph (no box, no size) — for a surface that sizes it itself
 *  (the item list's `text-2xl` flag). null when the scheme has no drawer. */
export function drawIcon(icon: string | null | undefined, glyphClass?: string): ReactNode | null {
  const separator = icon?.indexOf(':') ?? -1
  if (separator <= 0) return null
  return ICON_DRAWERS[icon!.slice(0, separator)]?.draw(icon!.slice(separator + 1), glyphClass) ?? null
}

/** Fail loudly when the server's icon scheme vocabulary and the drawers drift. */
export function assertIconSchemeDrawers(servedSchemes: readonly string[]): void {
  const served = new Set(servedSchemes)
  const unknown = servedSchemes.filter((scheme) => !ICON_DRAWERS[scheme])
  const unserved = Object.keys(ICON_DRAWERS).filter((scheme) => !served.has(scheme))
  if (unknown.length || unserved.length) {
    throw new Error(
      `icon scheme drawer mismatch: unknown=[${unknown.join(', ')}], unserved=[${unserved.join(', ')}]`,
    )
  }
}

/** Renders type icon metadata: "lucide:<PascalName>" → lucide icon;
 *  null/unknown → letter avatar (type-name initial). */
export function TypeIcon({ icon, name, className = '', color, servedSchemes }: {
  icon: string | null | undefined
  name: string
  className?: string
  /** Record-carried chip colour (hex from a conventional 'color' attribute) —
   *  tints the lucide glyph; absent → the muted theme default. */
  color?: string | null
  /** Scheme list carried by the served `icon` subtype row, when available. */
  servedSchemes?: readonly string[]
}) {
  const separator = icon?.indexOf(':') ?? -1
  const scheme = separator > 0 ? icon!.slice(0, separator) : null
  const payload = separator > 0 ? icon!.slice(separator + 1) : ''
  const drawer = scheme && (!servedSchemes || servedSchemes.includes(scheme))
    ? ICON_DRAWERS[scheme]
    : undefined
  const drawn = drawer?.draw(payload)
  if (drawer && drawn) {
    return (
      <span data-testid="type-icon" style={color ? { color } : undefined}
        data-icon-scheme={scheme}
        className={`inline-flex h-4 w-4 items-center justify-center ${drawer.className} ${color ? '' : 'text-muted-foreground'} ${className}`}>
        {drawn}
      </span>
    )
  }
  return (
    <span
      data-testid="type-icon"
      className={`inline-flex h-4 w-4 items-center justify-center rounded-sm text-[10px] font-semibold text-foreground ${bgFor(name)} ${className}`}
    >
      {(name[0] ?? '?').toUpperCase()}
    </span>
  )
}
